import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedLayout from '../components/layout/AuthenticatedLayout';
import { authService } from '../services/authService';
import { pricingService } from '../services/pricingService';
import './PricingPlans.css';

const PricingPlans = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Fetch pricing plans on component mount
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoadingPlans(true);
        const plans = await pricingService.getPricingPlans();
        setPricingPlans(plans);
      } catch (err) {
        console.error('Error fetching pricing plans:', err);
        setError('Failed to load pricing plans. Please try again later.');
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPricingPlans();
  }, []);

  const handleSelectPlan = async (planName, planPrice) => {
    setLoading(true);
    setError('');

    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');

    console.log('=== DEBUGGING INFO ===');
    console.log('Token from localStorage:', token ? `Token exists (${token.substring(0, 20)}...)` : 'No token found');
    console.log('User from localStorage:', user ? JSON.parse(user) : 'No user found');
    console.log('Full localStorage:', { ...localStorage });

    if (!token) {
      setError('Please login first to select a plan. No authentication token found.');
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    try {
      console.log('Creating Stripe checkout session for plan:', planName);

      // Create Stripe checkout session
      const response = await authService.createCheckoutSession({
        planName: planName,
        planPrice: planPrice,
        billingFrequency: 'monthly'
      });

      console.log('Checkout session response:', response);

      if (response && response.status && response.url) {
        // Redirect to Stripe checkout page
        window.location.href = response.url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      console.error('Error selecting plan:', err);

      // Handle 401 specifically
      if (err.response?.status === 401) {
        setError('Your session has expired. Please login again.');
        setTimeout(() => {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/login');
        }, 2000);
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to select plan. Please try again.';
        setError(errorMessage);
      }

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="pricing-plans-page">
        {/* Back Button */}
        <div className="mb-4 px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary transition-colors font-poppins text-sm font-medium"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
        </div>

        {error && (
          <div className="error-message general-error">
            <span className="message-text">{error}</span>
            <button type="button" className="message-close-btn" onClick={() => setError('')}>×</button>
          </div>
        )}

        {loadingPlans ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-5"></div>
          </div>
        ) : (
          <div className="pricing-plans-container">
            {pricingPlans.map((plan, index) => (
              <div
                key={plan._id}
                className={`pricing-card ${plan.highlighted ? 'popular-card' : ''}`}
              >
                {plan.highlighted && (
                  <div className="popular-badge">
                    <span>⭐</span>
                    <span>Most Popular</span>
                  </div>
                )}

                <div className="plan-header">
                  <h3 className="plan-title">{plan.name}</h3>
                  <p className="plan-subtitle">
                    {plan.tagline || plan.description}
                  </p>
                </div>

                <div className={`plan-price-section ${plan.highlighted ? 'popular-price' : ''}`}>
                  <div className="price-info">
                    <div className="price">
                      {plan.isCustom ? `Starting at $${plan.price}` : `$${plan.price}`}
                    </div>
                    <div className="price-period">
                      / {plan.period || 'Per Month'}
                    </div>
                  </div>
                  <p className="price-description">
                    {plan.isCustom
                      ? `${plan.reportsPerMonth}+ reports/month`
                      : `${plan.reportsPerMonth} reports/month`}
                  </p>
                </div>

                <button
                  className="get-started-btn"
                  onClick={() => handleSelectPlan(plan.name, plan.price)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Choose Plan'}
                </button>

                <div className="plan-features">
                  <p className="features-header">
                    {plan.baseIncludes || 'Included:'}
                  </p>
                  {plan.features && plan.features.length > 0 ? (
                    plan.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <span className="check-icon">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="feature-item">
                        <span className="check-icon">✓</span>
                        <span>24/7 Support</span>
                      </div>
                      <div className="feature-item">
                        <span className="check-icon">✓</span>
                        <span>Globally Compliant</span>
                      </div>
                      <div className="feature-item">
                        <span className="check-icon">✓</span>
                        <span>AI Insights</span>
                      </div>
                      <div className="feature-item">
                        <span className="check-icon">✓</span>
                        <span>{plan.reportsPerMonth} Reports per Month</span>
                      </div>
                    </>
                  )}
                </div>

                {plan.bestFor && (
                  <div className="best-for-section">
                    <p className="best-for-header">Best for:</p>
                    <p className="best-for-text">{plan.bestFor}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};

export default PricingPlans;
